import Callout from "components/callout/callout"
import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { AccessRole, Category, DeviceSize, SortDirection } from 'types'
import useCategories from "hooks/useCategories"
import useStorages from "hooks/useStorages"
import React, { JSX } from "react"
import IconImage from "components/image/IconImage"
import Section from "components/layout/section"
import { ReactComponent as CategoryIcon } from "assets/icons/category.svg"
import { ReactComponent as AddIcon } from "assets/icons/add.svg"
import Button from "components/inputs/button"
import DocList, { ViewMode } from "components/list/docList/docList"
import Doc from "components/list/docList/doc"
import TableList from "components/list/tableList/tableList"
import TableListRow from "components/list/tableList/tableListRow"
import TableListCell from "components/list/tableList/tableListCell"
import Tag from "components/tags/tag"
import { ReactComponent as ChevronRightIcon } from "assets/icons/chevron_right.svg"
import CategoryFilter from "./CategoryFilter"
import { logger } from "utils/secureLogger"
import useAccess from "@/hooks/useAccess"
import useWindowDimensions from "@/hooks/useWindowDimensions"

interface CategoryListProps {
    storageId: string
}

const CategoryList: React.FC<CategoryListProps> = ({ storageId }) => {

    const { t } = useTranslation()
    const { deviceSize } = useWindowDimensions()

    const { storage } = useStorages({ storageId })

    const {
        categories,
        allCategories,
        filteredCategories,
        categoriesStatus,
        categoriesInitialized,
        filterQuery,
        setFilterQuery,
        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,
        displayDeleted,
        setDisplayDeleted
    } = useCategories({ storageId })
    
    const {
        limitToRoles
    } = useAccess({ storageId: storageId || '' })

    // Allowed sort fields
    const allowedSortBy = ["categoryId", "name"] as const

    logger.debug("Categories loaded", { categoriesInitialized, categoryCounter: storage?.counters.categoryCounter })

    // Handle errors.
    if (!storageId) return <Callout type={`error`}>No storage id.</Callout>
    if (categoriesInitialized && !storage?.counters.categoryCounter && limitToRoles([AccessRole.OWNER, AccessRole.EDITOR])) {
        console.log("No categories - prompt to create one.")
        return (
            <Section design={`card-centered`}>
                <IconImage>
                    <CategoryIcon />
                </IconImage>
                <h2>{t(`titleNoCategories`)}</h2>
                <p>{t('categoryListNoCategories')}</p>
                <Link className="block w-full md:inline-block md:w-fit" to={`/storage/categories/create`}><Button wideOnMobile design="primary"><AddIcon /><span>{t('buttonCreateCategory')}</span></Button></Link>
            </Section>
        )
    }

    const printHierarchy = (categoryId: string, level: number, key: number): JSX.Element | null => {
        const categoryData = allCategories.find(c => c.id === categoryId)
        if (!categoryData) return null
        if (categoryData.isDeleted && !displayDeleted) {
            return (
                <React.Fragment key={key}>
            {allCategories.map((childCategory, key_ii) => {
                        if (childCategory.relations?.parentCategory === categoryId) {
                if (!childCategory.id) return null
                return printHierarchy(childCategory.id, level, key_ii)
                        }
                        return null
                    })}
                </React.Fragment>
            )
        }
        return (
            <React.Fragment key={key}>
                <TableListRow to={`/storage/categories/${categoryId}`} isDeleted={categoryData.isDeleted}>
                    <TableListCell>
                        {Array(level).fill('\u2014').join('')}{level ? ` ` : null}{categoryData.categoryId}
                    </TableListCell>
                    <TableListCell>
                        {categoryData.name}
                    </TableListCell>
                    <TableListCell>
                        {categoryData.description || '-'}
                    </TableListCell>
                </TableListRow>
        {allCategories.map((childCategory, key_ii) => {
                    if (childCategory.relations?.parentCategory === categoryId) {
            if (!childCategory.id) return null
            return printHierarchy(childCategory.id, level + 1, key_ii)
                    }
                    return null
                })}
            </React.Fragment>
        )
    }
    
    // Helper for flat categories (no hierarchy) for grid/list
    /*const rootCategories = allCategories.filter(([categoryId, categoryData]) =>
        !categoryData.parentCategory ||
        !allCategories.some(([id]) => id === categoryData.parentCategory)
    )*/

    // Helper to get parent category name
    const getParentCategoryName = (categoryData: Category) => {
        if (!categoryData.relations?.parentCategory) return null
        const parent = allCategories.find(c => c.id === categoryData.relations?.parentCategory)
        return parent ? parent.name : null
    }

    // Render the JSX.
    return (
        <DocList
            id={`category_list`}
            viewModes={deviceSize == DeviceSize.SM ? [ViewMode.List, ViewMode.Table] : [ViewMode.Grid, ViewMode.List, ViewMode.Table]}
            defaultViewMode={ViewMode.Grid}
            filterElement={
                <CategoryFilter
                    filterQuery={filterQuery}
                    setFilterQuery={setFilterQuery}
                    allowedSortBy={allowedSortBy}
                    sortBy={sortBy}
                    setSortBy={(v) => setSortBy(v as any)}
                    sortDirection={sortDirection}
                    setSortDirection={(v) => setSortDirection(v === 'asc' ? SortDirection.ASCENDING : SortDirection.DESCENDING)}
                    isLoading={!categoriesInitialized}
                    displayDeleted={displayDeleted}
                    setDisplayDeleted={setDisplayDeleted}
                />
            }
            showResults={(categoriesInitialized && filteredCategories.length > 0) || categoriesStatus !== `success`}
        >
            <div data-view-mode={ViewMode.Grid} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {categoriesInitialized && filteredCategories.length > 0
                    ? filteredCategories.map((categoryData, _key) => {
                        const categoryId = categoryData.id
                        if (categoryData.isDeleted && !displayDeleted) return null
                        const parentName = getParentCategoryName(categoryData)
                        return (
                            <Doc
                                key={categoryId}
                                // Show both id and name
                                title={`${categoryData.categoryId}: ${categoryData.name}`}
                                description={categoryData.description}
                                to={`/storage/categories/${categoryId}`}
                                isDeleted={categoryData.isDeleted}
                            >
                                {parentName && (
                                    <Tag colorScheme="gray">{t('labelParentCategory')}: {parentName}</Tag>
                                )}
                                <p>{categoryData.description}</p>
                            </Doc>
                        )
                    })
                    : Array.from({ length: 3 }).map((_, key) => (
                        <Doc key={key} isLoading={true}>
                            <p className="skeleton"></p>
                            <Button design="skeleton"></Button>
                        </Doc>
                    ))
                }
            </div>
            <div data-view-mode={ViewMode.List} className="flex flex-col gap-4">
                {categoriesInitialized && filteredCategories.length > 0
                    ? filteredCategories.map((categoryData, _key) => {
                        const categoryId = categoryData.id
                        if (categoryData.isDeleted && !displayDeleted) return null
                        const parentName = getParentCategoryName(categoryData)
                        return (
                            <Doc
                                key={categoryId}
                                // Show both id and name
                                title={`${categoryData.categoryId}: ${categoryData.name}`}
                                description={categoryData.description}
                                to={`/storage/categories/${categoryId}`}
                                isDeleted={categoryData.isDeleted}
                            >
                                {parentName && (
                                    <Tag colorScheme="gray">{t('labelParentCategory')}: {parentName}</Tag>
                                )}
                                <p>{categoryData.description}</p>
                            </Doc>
                        )
                    })
                    : Array.from({ length: 3 }).map((_, key) => (
                        <Doc key={key} isLoading={true}>
                            <p className="skeleton"></p>
                            <Button design="skeleton"></Button>
                        </Doc>
                    ))
                }
            </div>
            <div data-view-mode={ViewMode.Table}>
                <TableList>
                    <TableListRow isHeader={true}>
                        <TableListCell>{t('id')}</TableListCell>
                        <TableListCell>{t('name')}</TableListCell>
                        <TableListCell>{t('description')}</TableListCell>
                    </TableListRow>
                    {filteredCategories.map((categoryData, key) => {
                        if (!categoryData.id) return null
                        if (
                            categoryData.relations?.parentCategory &&
                            allCategories.some(c => c.id === categoryData.relations?.parentCategory)
                        ) return null
                        return printHierarchy(categoryData.id, 0, key)
                    })}
                    {categoriesStatus === `loading` && !categories.length &&
                        Array.from({ length: 3 }).map((_, key) => (
                            <TableListRow key={key}>
                                <TableListCell><p className="skeleton"></p></TableListCell>
                                <TableListCell><p className="skeleton"></p></TableListCell>
                                <TableListCell><p className="skeleton"></p></TableListCell>
                            </TableListRow>
                        ))
                    }
                </TableList>
            </div>
        </DocList>
    )
}

export default CategoryList

